import { useMemo, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import {
  ComponentRendering,
  Field,
  ImageFieldValue,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import {
  ComponentProps,
  JabraIcon,
  resolveBackgroundStyle,
  useBreakpoint,
} from '@gn-audio/jss-components';

type EnterpriseImageSpot = {
  backgroundColor: Field<string>;
  icon: Field<keyof typeof icons>;
  description: Field<string>;
  mobile: Field<ImageFieldValue>;
  tablet: Field<ImageFieldValue>;
  desktop: Field<ImageFieldValue>;
} & ComponentRendering;
type EnterpriseImageSpotProps = {
  fields: EnterpriseImageSpot;
} & ComponentProps;

gsap.registerPlugin(useGSAP, ScrollTrigger);

const EnterpriseImageSpot = ({ fields }: EnterpriseImageSpotProps) => {
  const boxRef = useRef(null);
  const backgroundColor = fields.backgroundColor;
  const spotBackgroundStyle = resolveBackgroundStyle(backgroundColor?.value as string);

  const iconName = fields.icon;
  const description = fields.description?.value;

  const { isAboveXl } = useBreakpoint('xl', true);
  const { isAboveMd } = useBreakpoint('md', true);
  const { isAboveXs } = useBreakpoint('xs', true);

  const image = useMemo(() => {
    const getFirstValidImage = (...images: (ImageFieldValue | undefined)[]) =>
      images.find((img) => img?.src) || null;
    if (isAboveXl) {
      return getFirstValidImage(fields.desktop?.value, fields.tablet?.value, fields.mobile?.value);
    } else if (isAboveMd) {
      return getFirstValidImage(fields.tablet?.value, fields.desktop?.value, fields.mobile?.value);
    } else {
      return getFirstValidImage(fields.mobile?.value, fields.tablet?.value, fields.desktop?.value);
    }
  }, [isAboveMd, isAboveXl, fields]);

  useGSAP(() => {
    const current = boxRef.current;
    gsap.from(current, {
      y: '+=100',
      scale: 1.2,
      ease: 'power1.inOut',
    });
    gsap.to(current, {
      scrollTrigger: {
        trigger: current,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 2,
      },
      scale: 1,
      clipPath: 'circle(100%)',
    });
  }, []);
  return (
    <div className="test relative h-screen w-full overflow-hidden" style={spotBackgroundStyle}>
      <img
        ref={boxRef}
        className="absolute h-screen w-full scale-110 object-cover"
        src={image?.src}
        alt={image?.alt as string}
      />
      {description && (
        <div className="z-1 absolute flex h-full w-full items-end justify-end">
          <div className="m-3 flex w-[420px] items-center justify-start rounded-xl bg-white p-6 xs:m-10">
            {isAboveXs && !!iconName?.value && (
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-[#0000000d]">
                <JabraIcon className="h-12 w-12" Icon={icons?.[iconName.value]} />
              </div>
            )}
            <div
              className="pl-8 text-base leading-6"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default withDatasourceCheck()(EnterpriseImageSpot);
